import { Trash2, Inbox, ChevronDown, ChevronRight } from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import FeedCard from "./FeedCard";
import ExportMenu from "./ExportMenu";
import { motion, AnimatePresence } from "framer-motion";

export default function FeedCardList({ posts, loading, onClearFeed, hasMore, loadingMore, onLoadMore, totalCount, activeFilterCount, onClearFilters, filters, onToggleFilter, aggregation }) {
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [expandedClusters, setExpandedClusters] = useState(new Set());

  const handleToggleExpand = useCallback((postId) => {
    setExpandedPostId((prev) => (prev === postId ? null : postId));
  }, []);

  const toggleCluster = useCallback((clusterId) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  }, []);

  // Group posts by cluster
  const { visiblePosts, clusterRelated } = useMemo(() => {
    const related = {};
    const visible = [];
    const seenPrimaryClusters = new Set();

    for (const post of posts) {
      const cid = post.cluster_id;
      const rank = post.cluster_rank ?? 0;

      if (!cid || rank === 0) {
        visible.push(post);
        if (cid) seenPrimaryClusters.add(cid);
      } else {
        if (!related[cid]) related[cid] = [];
        related[cid].push(post);
      }
    }

    // Show orphan secondaries (primary not in current page) as normal cards
    for (const [cid, rPosts] of Object.entries(related)) {
      if (!seenPrimaryClusters.has(cid)) {
        visible.push(...rPosts);
        delete related[cid];
      }
    }

    return { visiblePosts: visible, clusterRelated: related };
  }, [posts]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <span
          className="cw-spin inline-block rounded-full border-2"
          style={{
            width: 24,
            height: 24,
            borderColor: '#30363d',
            borderTopColor: '#22c55e',
          }}
        />
        <span className="font-mono text-xs" style={{ color: '#8b949e' }}>
          Loading feed...
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* Feed header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2
            className={`font-mono text-sm tracking-[0.08em] uppercase font-medium ${activeFilterCount > 0 ? 'cursor-pointer hover:opacity-80' : ''}`}
            style={{ color: '#e6edf3' }}
            onClick={activeFilterCount > 0 ? onClearFilters : undefined}
            title={activeFilterCount > 0 ? 'Click to clear all filters' : undefined}
            data-testid="feed-header-title"
          >
            Analysis Feed
          </h2>
          {(totalCount > 0 || posts.length > 0) && (
            <span
              className="font-mono text-[10px] px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: '#21262d',
                color: '#8b949e',
                border: '1px solid #30363d',
              }}
              data-testid="feed-count-badge"
            >
              {activeFilterCount > 0 ? `${totalCount} total` : (totalCount || posts.length)}
            </span>
          )}
          {activeFilterCount > 0 && (
            <span
              className="font-mono text-[10px]"
              style={{ color: '#4ade80' }}
              data-testid="feed-matching-count"
            >
              &middot; {posts.length} matching
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {posts.length > 0 && (
            <ExportMenu posts={posts} aggregation={aggregation} />
          )}
          {posts.length > 0 && (
            <button
              data-testid="clear-feed-button"
            onClick={onClearFeed}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-[6px] font-mono text-[11px] tracking-[0.08em] uppercase transition-colors duration-150"
            style={{
              color: '#8b949e',
              border: '1px solid #30363d',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1c2333';
              e.currentTarget.style.color = '#ef4444';
              e.currentTarget.style.borderColor = '#7f1d1d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#8b949e';
              e.currentTarget.style.borderColor = '#30363d';
            }}
          >
            <Trash2 size={12} />
            Clear Feed
          </button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {posts.length === 0 ? (
        <div
          data-testid="feed-empty-state"
          className="flex flex-col items-center justify-center py-24 gap-3"
        >
          <Inbox size={40} style={{ color: '#30363d' }} />
          <p className="font-mono text-sm" style={{ color: '#8b949e' }}>
            No analyses yet
          </p>
          <p className="font-sans text-xs" style={{ color: '#484f58' }}>
            Headlines will appear automatically, or paste one in the sidebar
          </p>
        </div>
      ) : (
        <>
          <div data-testid="feed-list-scroll-area" className="space-y-3">
            <AnimatePresence initial={false}>
              {visiblePosts.map((post) => {
                const related = clusterRelated[post.cluster_id] || [];
                const isClusterOpen = expandedClusters.has(post.cluster_id);
                return (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <FeedCard
                      post={post}
                      isExpanded={expandedPostId === post.post_id}
                      onToggleExpand={handleToggleExpand}
                      filters={filters}
                      onToggleFilter={onToggleFilter}
                    />
                    {related.length > 0 && (
                      <div className="ml-4 sm:ml-6 mt-1">
                        <button
                          data-testid={`cluster-toggle-${post.cluster_id}`}
                          onClick={() => toggleCluster(post.cluster_id)}
                          className="flex items-center gap-1 px-2 py-1 font-mono text-[11px] transition-colors"
                          style={{ color: '#8b949e' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#c9d1d9'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#8b949e'; }}
                        >
                          <ChevronRight
                            size={12}
                            style={{
                              transform: isClusterOpen ? 'rotate(90deg)' : 'rotate(0)',
                              transition: 'transform 150ms ease',
                            }}
                          />
                          {related.length} related {related.length === 1 ? 'report' : 'reports'}
                        </button>
                        {isClusterOpen && (
                          <div className="space-y-2 mt-1 pl-2" style={{ borderLeft: '2px solid #21262d' }}>
                            {related.map((rp) => (
                              <FeedCard
                                key={rp.post_id}
                                post={rp}
                                isExpanded={expandedPostId === rp.post_id}
                                onToggleExpand={handleToggleExpand}
                                filters={filters}
                                onToggleFilter={onToggleFilter}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center py-6">
              <button
                data-testid="load-more-button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 px-5 py-2.5 rounded-[6px] font-mono text-xs tracking-[0.08em] uppercase transition-colors duration-150"
                style={{
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                  color: loadingMore ? '#484f58' : '#c9d1d9',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!loadingMore) {
                    e.currentTarget.style.backgroundColor = '#1c2333';
                    e.currentTarget.style.borderColor = '#484f58';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#161b22';
                  e.currentTarget.style.borderColor = '#30363d';
                }}
              >
                {loadingMore ? (
                  <>
                    <span
                      className="cw-spin inline-block rounded-full border-2"
                      style={{ width: 12, height: 12, borderColor: '#30363d', borderTopColor: '#22c55e' }}
                    />
                    Loading...
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />
                    Load More
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
